import { forwardRef } from 'react';
import Tooltip from '@/molecules/modals/Tooltip/Tooltip';
import Size from '@enums/Size';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import MuiChip from '@mui/material/Chip';
import ChipSize from './ChipSize';
import ChipColors from './colors/Chip';

const Chip = forwardRef(function Chip(
  {
    id,
    fontWeight = 500,
    color = 'primary',
    colorProps,
    description,
    expanded = true,
    name,
    extendLeftPadding,
    onClick,
    onDelete,
    size = Size.MEDIUM,
    tooltipPlacement = 'top',
    sx = {},
  },
  ref
) {
  const sizeProps = ChipSize[size];
  const chipColor = colorProps ?? ChipColors[color];
  const leftPadding = extendLeftPadding ? 0.5 : 0;
  const extraPadding = extendLeftPadding ? 0.125 : 0;

  const handleDelete = (event) => {
    onDelete?.(event, id);
  };

  const handleClick = (event) => {
    if (onClick) {
      onClick?.(event, id);
      return;
    }

    handleDelete(event);
  };

  const interactive = onDelete || onClick;

  return (
    <Tooltip
      arrow
      title={description}
      position={tooltipPlacement}
      color="tertiary"
    >
      <MuiChip
        deleteIcon={<ClearRoundedIcon color={chipColor.normal.color} />}
        label={expanded ? name : null}
        onClick={handleClick}
        onDelete={onDelete ? handleDelete : null}
        ref={ref}
        sx={{
          ...chipColor.normal,
          borderRadius: extendLeftPadding ? '3px' : sizeProps.borderRadius,
          cursor: interactive ? 'pointer' : 'inherit',
          display: expanded ? 'inline-flex' : 'inline-block',
          fontSize: expanded ? sizeProps.fontSize : '0.75rem',
          fontWeight: fontWeight,
          height: expanded ? sizeProps.height : '0.75rem',
          width: expanded ? 'auto' : '0.75rem',
          minWidth: expanded ? 'auto' : '0.75rem',
          lineHeight: expanded ? sizeProps.lineHeight : '0.75rem',
          pt: sizeProps.pt,
          paddingRight: expanded ? sizeProps.pr : 0,
          paddingLeft: expanded ? sizeProps.pl + leftPadding : 0,
          '& .MuiChip-label': {
            padding: 0,
            pt: extraPadding,
          },
          '& .MuiChip-deleteIcon': {
            color: chipColor.normal.color,
            width: '1rem',
            fontSize: '0.95rem',
            marginRight: 0,
            marginLeft: 0.5,
          },
          '&:hover': interactive
            ? { ...chipColor.hover }
            : { ...chipColor.normal },
          '&:focus': interactive
            ? { ...chipColor.focus }
            : { ...chipColor.normal },
          ...sx,
        }}
      />
    </Tooltip>
  );
});

export default Chip;
